import type { Academy, AcademyOperatingProfileInput } from '@court-sync/shared';
import {
  personalizeSetupTaskCopy,
  resolveLessonsSetupTaskHref,
  resolveSetupTaskTourKey,
  shouldShowProgramsSetupTask,
  shouldShowTermsSetupTask,
  sortSetupTasksByProfile,
  type AcademySetupPageTourKey,
  type AcademySetupTaskId,
} from '@court-sync/shared';
export type AcademyOwnerSetupTaskId = AcademySetupTaskId;

export interface AcademyOwnerSetupTask {
  id: AcademyOwnerSetupTaskId;
  title: string;
  description: string;
  href: string;
  done: boolean;
  /** Page tour that auto-starts on first visit to `href` (see tours skill). */
  pageTourKey?: AcademySetupPageTourKey | null;
}

export interface BuildAcademyOwnerSetupTasksInput {
  academy: Pick<Academy, 'operatingProfile'> | null;
  done: Record<AcademySetupTaskId, boolean>;
  hasPaymentProcessingFeature: boolean;
  hasMultiLocationFeature?: boolean;
  operatingProfile?: AcademyOperatingProfileInput | null;
}

function resolveOperatingProfile(input: BuildAcademyOwnerSetupTasksInput): AcademyOperatingProfileInput | null | undefined {
  return input.operatingProfile ?? input.academy?.operatingProfile ?? null;
}

function isSoloCoachProfile(profile: AcademyOperatingProfileInput | null | undefined): boolean {
  return profile?.operatingRole === 'solo_coach';
}

function wantsMultiLocation(profile: AcademyOperatingProfileInput | null | undefined): boolean {
  if (profile?.locationCountBand === '2-3' || profile?.locationCountBand === '4+') return true;
  return (profile?.desiredWorkflows ?? []).includes('multi_location');
}

/**
 * Builds ordered setup tasks for the academy owner dashboard checklist.
 * Order and copy personalize from the operating profile when available.
 */
export function buildAcademyOwnerSetupTasks(input: BuildAcademyOwnerSetupTasksInput): AcademyOwnerSetupTask[] {
  const {
    done,
    hasPaymentProcessingFeature,
    hasMultiLocationFeature = false,
  } = input;

  const profile = resolveOperatingProfile(input);
  const soloCoach = isSoloCoachProfile(profile);
  const showLocationsTask = hasMultiLocationFeature && wantsMultiLocation(profile);
  const showProgramsTask = shouldShowProgramsSetupTask(profile);

  const paymentTitle = hasPaymentProcessingFeature
    ? 'Connect Stripe for online payments'
    : 'Add payment collection instructions';
  const paymentDescription = hasPaymentProcessingFeature
    ? 'Complete Stripe Connect so students can pay when booking.'
    : 'Tell students how to pay (e.g. cash, bank transfer) in booking confirmations.';
  const paymentHref = '/admin/payments?tab=settings';

  const sportsTask: AcademyOwnerSetupTask = {
    id: 'sports',
    title: 'Configure sports',
    description: 'Confirm which racquet sports you offer and set a primary sport.',
    href: '/admin/organization/settings?tab=sports',
    done: done.sports,
  };

  const courtsTask: AcademyOwnerSetupTask = {
    id: 'courts',
    title: 'Add courts',
    description: showLocationsTask
      ? 'Create courts and assign which sports each court supports.'
      : 'Create at least one court for scheduling and lessons.',
    href: '/admin/courts',
    done: done.courts,
  };

  const locationsTask: AcademyOwnerSetupTask = {
    id: 'locations',
    title: 'Add locations',
    description: 'Set up each venue when you run lessons at more than one site.',
    href: '/admin/courts?tab=locations',
    done: done.locations,
  };

  const coachesTask: AcademyOwnerSetupTask = {
    id: 'coaches',
    title: 'Add coaches',
    description: profile?.coachesNeedPortalAccess
      ? 'Invite coaches so they can log in, view their schedule, and track students.'
      : 'Add coach names and contact details for scheduling.',
    href: '/admin/staff',
    done: done.coaches,
  };

  const ageGroupsTask: AcademyOwnerSetupTask = {
    id: 'age_groups',
    title: 'Set up age groups',
    description:
      'Choose how to group students (by age, ball colour, or custom labels) for lessons and calendars.',
    href: '/admin/academy/settings?tab=general',
    done: done.age_groups,
  };

  const termsTask: AcademyOwnerSetupTask = {
    id: 'terms',
    title: 'Create a term',
    description: 'Define a term so group lessons can run on dates.',
    href: '/admin/terms',
    done: done.terms,
  };

  const lessonsTask: AcademyOwnerSetupTask = {
    id: 'lessons',
    title: 'Create lessons',
    description: 'Add active lessons students can book.',
    href: resolveLessonsSetupTaskHref(profile),
    done: done.lessons,
  };

  const programsTask: AcademyOwnerSetupTask = {
    id: 'programs',
    title: 'Set up development programs',
    description: 'Create pathways with target skills and link them to lessons.',
    href: '/admin/programs',
    done: done.programs,
  };

  const paymentsTask: AcademyOwnerSetupTask = {
    id: 'payments',
    title: paymentTitle,
    description: paymentDescription,
    href: paymentHref,
    done: done.payments,
  };

  const profileTask: AcademyOwnerSetupTask = {
    id: 'academy_profile',
    title: 'Customise your academy',
    description: 'Add details, branding, and review settings for policies and notifications.',
    href: '/admin/organization/settings',
    done: done.academy_profile,
  };

  const tasks: AcademyOwnerSetupTask[] = [
    profileTask,
    sportsTask,
    courtsTask,
    ...(showLocationsTask ? [locationsTask] : []),
    ...(!soloCoach ? [coachesTask] : []),
    ageGroupsTask,
    ...(shouldShowTermsSetupTask(profile) ? [termsTask] : []),
    ...(showProgramsTask ? [programsTask] : []),
    lessonsTask,
    paymentsTask,
  ];

  const sorted = sortSetupTasksByProfile(tasks, profile);
  return sorted.map((task) => {
    const personalized = personalizeSetupTaskCopy(task, profile, { hasPaymentProcessingFeature });
    return {
      ...personalized,
      pageTourKey: resolveSetupTaskTourKey(personalized.id, profile),
    };
  });
}
